/**
 * CopyGuestLinkButton
 * Copy-to-clipboard button for an external user's personal guest link. The
 * URL comes from the delegate's `Delegate.link.url` (guestTokenApi list) or
 * the user's `GuestLinkSummary.url` (externalLinkApi); disabled while the user
 * has no link to copy.
 *
 * Shared by the work order and project assignment displays and Externa användare.
 */
import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import { Copy } from 'lucide-react';

interface CopyGuestLinkButtonProps {
    url?: string | null;
    /** Text next to the icon; icon-only when omitted */
    label?: string;
    className?: string;
}

const CopyGuestLinkButton: React.FC<CopyGuestLinkButtonProps> = ({ url, label, className = 'ms-2 py-0' }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <Button
            variant={copied ? 'success' : 'outline-secondary'}
            size="sm"
            className={className}
            onClick={handleCopy}
            disabled={!url}
            title={url ? 'Kopiera personlig länk' : 'Ingen länk att kopiera'}
        >
            {copied ? 'Kopierad!' : (
                <>
                    <Copy size={label ? 14 : 12} className={label ? 'me-1' : undefined} />
                    {label}
                </>
            )}
        </Button>
    );
};

export default CopyGuestLinkButton;
